import { ReactNode, useCallback, useState } from 'react';

import CheckCircleIcon from '@mui/icons-material/CheckCircleRounded';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Menu, Tooltip } from '@mui/material';

import ScrollArea from '@/shared/ui/ScrollArea';

import { CAMPAIGN_PERIOD_FIELD_ID } from './GeneratikTable.constants';
import { DropdownButton, DropdownMenuItem, ToolbarButton } from './GeneratikTableToolbar.styles';
import { accentCheckIconSx, dropdownMenuPaperSx } from './ToolbarDropdown.styles';

export interface DropdownOption {
  id: string;
  label: string;
}

export interface ToolbarDropdownProps {
  label: string;
  icon: ReactNode;
  /** Ignored when `customContent` is supplied. */
  options?: DropdownOption[];
  selectedId?: string | null;
  onSelect?: (id: string | null) => void;
  collapsed?: boolean;
  showNoneOption?: boolean;
  noneLabel?: string;
  variant?: 'button' | 'dropdown';
  customContent?: (close: () => void) => ReactNode;
  campaignColumnVisibility?: Record<string, boolean>;
  onDisabledItemClick?: (fieldId: string) => void;
  disabled?: boolean;
  'data-testid'?: string;
}

const ToolbarDropdown = ({
  label,
  icon,
  options = [],
  selectedId,
  onSelect,
  collapsed = false,
  showNoneOption = false,
  noneLabel = 'None',
  variant = 'dropdown',
  customContent,
  campaignColumnVisibility,
  onDisabledItemClick,
  disabled = false,
  'data-testid': dataTestId,
}: ToolbarDropdownProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleSelect = useCallback(
    (id: string | null) => {
      onSelect?.(id);
      handleClose();
    },
    [onSelect, handleClose],
  );

  const handleDisabledItemClick = useCallback(() => {
    handleClose();
    onDisabledItemClick?.(CAMPAIGN_PERIOD_FIELD_ID);
  }, [handleClose, onDisabledItemClick]);

  const ButtonComponent = variant === 'button' ? ToolbarButton : DropdownButton;

  return (
    <>
      <Tooltip title={collapsed ? label : ''} arrow disableHoverListener={!collapsed}>
        <ButtonComponent
          collapsed={collapsed}
          startIcon={icon}
          endIcon={<KeyboardArrowDownIcon />}
          onClick={handleClick}
          disabled={disabled}
          data-testid={dataTestId}
        >
          <span className="button-text">{label}</span>
        </ButtonComponent>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{ paper: { sx: dropdownMenuPaperSx } }}
      >
        <ScrollArea maxHeight="18.75rem">
          {customContent?.(handleClose) || (
            <>
              {showNoneOption && (
                <DropdownMenuItem onClick={() => handleSelect(null)} selected={!selectedId}>
                  <span>{noneLabel}</span>
                  {!selectedId && <CheckCircleIcon sx={accentCheckIconSx} />}
                </DropdownMenuItem>
              )}
              {[...options]
                .sort((a, b) => a.label.localeCompare(b.label))
                .map((option) => (
                  <Tooltip
                    key={option.id}
                    title={
                      campaignColumnVisibility?.[option.id] === false
                        ? 'Campaign period is required'
                        : undefined
                    }
                  >
                    <span
                      onClick={
                        campaignColumnVisibility?.[option.id] === false
                          ? handleDisabledItemClick
                          : undefined
                      }
                    >
                      <DropdownMenuItem
                        onClick={() => handleSelect(option.id)}
                        selected={selectedId === option.id}
                        disabled={campaignColumnVisibility?.[option.id] === false}
                      >
                        <span>{option.label}</span>
                        {selectedId === option.id && <CheckCircleIcon sx={accentCheckIconSx} />}
                      </DropdownMenuItem>
                    </span>
                  </Tooltip>
                ))}
            </>
          )}
        </ScrollArea>
      </Menu>
    </>
  );
};

export default ToolbarDropdown;
